import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Info, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { formatMoney, formatNumber } from '../lib/format';
import { pointsText, timesBundles, type RedemptionType } from '../lib/wallet';
import { Badge, Button, Card, CardHeader, ErrorState, Field, Input, LoadingBlock, Select, useToast } from './ui';

type Name =
  | 'quarterDayPenaltyPoints'
  | 'halfDayPenaltyPoints'
  | 'fullDayPenaltyPoints'
  | 'leaveDayPoints'
  | 'cashPointsPerBundle'
  | 'cashAmountPerBundle'
  | 'cashCurrency';

interface WalletSettings {
  values: Record<Name, number | string | null>;
  priced: Record<RedemptionType, boolean>;
  cashReach: { currency: string; otherCurrency: number; otherCurrencies: string[]; noSalary: number } | null;
  ledger: { wallets: number; mismatched: number; points: number };
}

interface Currency {
  code: string;
  name: string;
  nameAr: string | null;
  decimalPlaces: number;
}

const POINT_FIELDS: Array<{ name: Name; type: RedemptionType }> = [
  { name: 'quarterDayPenaltyPoints', type: 'REMOVE_QUARTER_DAY_PENALTY' },
  { name: 'halfDayPenaltyPoints', type: 'REMOVE_HALF_DAY_PENALTY' },
  { name: 'fullDayPenaltyPoints', type: 'REMOVE_FULL_DAY_PENALTY' },
  { name: 'leaveDayPoints', type: 'ADD_ONE_LEAVE_DAY' },
];

const NAMES: Name[] = [...POINT_FIELDS.map((f) => f.name), 'cashPointsPerBundle', 'cashAmountPerBundle', 'cashCurrency'];
const keyOf = (name: Name) => `wallet.${name}`;
const text = (value: unknown) => (value === null || value === undefined ? '' : String(value));

/**
 * Settings › Wallet & Points (CR-WALLET-001 §4): what each reward costs in points,
 * and the cash a bundle of points is worth. Each starts empty, and an empty one is
 * not offered. A save is checked as a whole by the API and every change audited;
 * a redemption already made keeps the price it had. Below, the two things an
 * administrator should know: who cannot take cash (it goes with the salary, in its
 * currency), and whether every wallet matches its history.
 */
export function WalletSettingsPanel() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['wallet', 'settings'],
    queryFn: () => api.get<WalletSettings>('/wallet/settings'),
  });
  const { data: currencies } = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get<Currency[]>('/currencies'),
    staleTime: 5 * 60_000,
  });

  const [draft, setDraft] = useState<Partial<Record<Name, string>>>({});
  const [problems, setProblems] = useState<Array<{ key: string; reason: string; limit?: number }>>([]);
  useEffect(() => {
    setDraft({});
    setProblems([]);
  }, [data]);

  const current = (name: Name) => (name in draft ? draft[name]! : text(data?.values[name]));
  const changed = NAMES.filter((name) => name in draft && draft[name] !== text(data?.values[name]));

  const save = useMutation({
    mutationFn: () =>
      api.put<WalletSettings>('/wallet/settings', {
        values: Object.fromEntries(changed.map((name) => [keyOf(name), draft[name]!.trim()])),
      }),
    onSuccess: () => {
      toast.success(t('settings.saved'));
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
    },
    onError: (e: Error) => {
      const found = e instanceof ApiError ? ((e.details as { problems?: typeof problems } | undefined)?.problems ?? []) : [];
      setProblems(found);
      toast.error(found.length ? t('settings.fixMarked') : e.message);
    },
  });

  if (isLoading) return <LoadingBlock />;
  if (error || !data) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const problemFor = (name: Name): string | null => {
    const p = problems.find((x) => x.key === keyOf(name));
    return p ? t(`settings.problem.${p.reason}` as TranslationKey, { limit: p.limit ?? '' }) : null;
  };
  const set = (name: Name, value: string) => setDraft({ ...draft, [name]: value });
  const offered = (type: RedemptionType) =>
    data.priced[type] ? (
      <Badge tone="success">{t('settings.wallet.offered')}</Badge>
    ) : (
      <Badge tone="neutral">{t('settings.wallet.notOffered')}</Badge>
    );

  // The example the employees' wallet shows, worked out from what is on the form now.
  const perBundle = Number(current('cashPointsPerBundle'));
  const amount = current('cashAmountPerBundle');
  const currency = current('cashCurrency');
  const example =
    Number.isInteger(perBundle) && perBundle > 0 && Number(amount) > 0 && currency
      ? t('settings.wallet.cashExample', {
          points: pointsText(perBundle * 2, locale, t),
          amount: formatMoney(timesBundles(amount, 2), currency, locale),
        })
      : null;

  return (
    <>
      <Card>
        <CardHeader
          title={t('settings.wallet')}
          subtitle={t('settings.walletNote')}
          action={
            <Button
              variant="primary"
              icon={<Save className="h-4 w-4" />}
              disabled={changed.length === 0}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {t('common.save')}
            </Button>
          }
        />

        <p className="mb-5 flex items-start gap-2 rounded-md border hairline bg-[var(--surface-sunken)] px-3 py-2 text-body-sm text-[var(--text-secondary)]">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
          {t('settings.wallet.earning')}
        </p>

        <div className="grid gap-5 sm:grid-cols-2">
          {POINT_FIELDS.map(({ name, type }) => (
            <Field
              key={name}
              label={
                <span className="flex flex-wrap items-center gap-2">
                  {t(`settings.wallet.${name}` as TranslationKey)}
                  {offered(type)}
                </span>
              }
              hint={
                <>
                  <span className="block">{type === 'ADD_ONE_LEAVE_DAY' ? t('settings.wallet.leaveHint') : t('settings.wallet.unsetHint')}</span>
                  <span className="font-mono text-mono-sm" dir="ltr">
                    {keyOf(name)}
                  </span>
                </>
              }
              error={problemFor(name)}
            >
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  step={1}
                  className="max-w-[10rem] tabular"
                  value={current(name)}
                  onChange={(e) => set(name, e.target.value)}
                />
                <span className="text-body-sm text-muted">{t('wallet.pointsUnit')}</span>
              </div>
            </Field>
          ))}
        </div>

        <div className="mt-6 border-t hairline pt-5">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <h4 className="text-title text-[var(--text-primary)]">{t('wallet.type.CASH_REWARD')}</h4>
            {offered('CASH_REWARD')}
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label={t('settings.wallet.cashPointsPerBundle')} error={problemFor('cashPointsPerBundle')}>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                className="tabular"
                value={current('cashPointsPerBundle')}
                onChange={(e) => set('cashPointsPerBundle', e.target.value)}
              />
            </Field>
            <Field label={t('settings.wallet.cashAmountPerBundle')} error={problemFor('cashAmountPerBundle')}>
              <Input
                type="number"
                inputMode="decimal"
                min={0.01}
                step={0.01}
                className="tabular"
                value={current('cashAmountPerBundle')}
                onChange={(e) => set('cashAmountPerBundle', e.target.value)}
              />
            </Field>
            <Field label={t('settings.wallet.cashCurrency')} error={problemFor('cashCurrency')}>
              <Select value={current('cashCurrency')} onChange={(e) => set('cashCurrency', e.target.value)}>
                <option value="">{t('settings.wallet.noCurrency')}</option>
                {(currencies ?? []).map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {locale === 'ar' ? c.nameAr || c.name : c.name}
                  </option>
                ))}
                {/* A currency switched off since keeps showing what is saved. */}
                {current('cashCurrency') && !(currencies ?? []).some((c) => c.code === current('cashCurrency')) && (
                  <option value={current('cashCurrency')}>{current('cashCurrency')}</option>
                )}
              </Select>
            </Field>
          </div>
          <p className="mt-3 text-caption text-muted">{t('settings.wallet.cashHint')}</p>
          {example && <p className="mt-1 text-caption text-[var(--text-secondary)] tabular">{example}</p>}
        </div>
      </Card>

      {data.cashReach && (data.cashReach.otherCurrency > 0 || data.cashReach.noSalary > 0) && (
        <p className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-bg px-4 py-3 text-body-sm text-warning-fg dark:bg-warning/14 dark:text-warning-dark">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          {t('settings.wallet.cashReach', {
            currency: data.cashReach.currency,
            n: data.cashReach.otherCurrency,
            list: data.cashReach.otherCurrencies.join(', ') || '—',
            m: data.cashReach.noSalary,
          })}
        </p>
      )}

      <Card>
        <CardHeader title={t('settings.wallet.ledger')} />
        {data.ledger.mismatched === 0 ? (
          <p className="flex items-center gap-2 text-body-sm text-[var(--text-secondary)]">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-success-fg dark:text-success" aria-hidden />
            {t('settings.wallet.ledgerOk', {
              n: formatNumber(data.ledger.wallets, locale),
              points: pointsText(data.ledger.points, locale, t),
            })}
          </p>
        ) : (
          <p className="flex items-center gap-2 text-body-sm text-error-fg dark:text-error-dark">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            {t('settings.wallet.ledgerBad', { bad: data.ledger.mismatched, n: data.ledger.wallets })}
          </p>
        )}
      </Card>
    </>
  );
}
